import React, { useCallback, memo, useMemo, useEffect } from "react";
import { isEmpty } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import {
	change,
	Field,
	FormSection,
	formValueSelector,
	getFormInitialValues,
} from "redux-form";

import { CustomDropdownByValue, TextWithLoader } from "components";
import { FieldWithReadOnlyMode } from "components/formFields";
import { FormColumn, FormRow } from "components/layout";
import {
	editResidualsPayoutDetailsSet,
	editResidualsStatusSet,
	hasPermissions,
} from "helpers";
import { partnerStatus } from "mappers";
import { usePayoutMethodsQuery } from "queries";
import { uiActions } from "store";
import { getCurrentUserId } from "store/user/user.reducer";
import { ResidualsStatusTypes } from "types";
import { PayoutMethodTypes, WithholdStatuses } from "types/enums";
import { required } from "validators";

import { AchFields } from "./achField";
import { CheckFields } from "./checkFields";
import { DomesticWireFields } from "./domesticWireFields";
import { InternationalWireFields } from "./internationalWireFields";

type PayoutInfoSectionProps = {
	loading: boolean;
	formName: string;
	partnerType: string;
	statusFieldName: string;
	residualsStatus?: number;
	residualsStatusUpdatedBy?: { [key: string]: any };
};

const PayoutInfoSection = ({
	loading,
	formName,
	partnerType,
	statusFieldName,
	residualsStatus,
	residualsStatusUpdatedBy,
}: PayoutInfoSectionProps) => {
	const dispatch = useDispatch();

	const selector = formValueSelector(formName);
	const currentUserId = useSelector(getCurrentUserId);

	const formInitialValues = useSelector(state =>
		getFormInitialValues(formName)(state),
	);
	const statusField = useSelector(state => selector(state, statusFieldName));
	const paymentInfo = useSelector(state => selector(state, "payment_info"));

	const getStatusValue = fieldData =>
		fieldData ? fieldData.value || parseInt(fieldData) : undefined;

	const statusInitialValue = getStatusValue(
		formInitialValues?.[statusFieldName],
	);
	const statusValue = getStatusValue(statusField);

	useEffect(() => {
		if (
			statusValue !== undefined &&
			statusInitialValue !== undefined &&
			statusValue === statusInitialValue
		) {
			return;
		}

		if (statusValue === partnerStatus.prospectiveAgent) {
			dispatch(change(formName, "payment_info.residuals_status", "1"));
			dispatch(change(formName, "payment_info.withhold_reason", "1"));
		}

		if (statusValue === partnerStatus.terminatedAgent) {
			dispatch(change(formName, "payment_info.residuals_status", "1"));
			dispatch(change(formName, "payment_info.withhold_reason", "2"));
		}
	}, [dispatch, formName, statusInitialValue, statusValue]);

	const paymentInfoType = useMemo(() => {
		return parseInt(paymentInfo?.payment_method_type);
	}, [paymentInfo?.payment_method_type]);

	const isWithholdResiduals = useMemo(
		() =>
			paymentInfo?.residuals_status === "1" ||
			paymentInfo?.residuals_status === 1,
		[paymentInfo?.residuals_status],
	);

	const isResidualsStatusDisabled = !hasPermissions(editResidualsStatusSet);

	const isPayoutDetailsEditable = hasPermissions(
		editResidualsPayoutDetailsSet,
	);

	const residualsStatuses = ResidualsStatusTypes.getOptions();

	const withholdStatuses = WithholdStatuses.getOptions();

	// prettier-ignore
	const {
		data: payoutMethods,
		isLoading: isLoadingPayoutMethods,
	} = usePayoutMethodsQuery({options:{ retry: 0 }});

	const paymentMethodType = useMemo(
		() =>
			payoutMethods?.find(method => method.value === paymentInfoType)
				?.displayName,
		[paymentInfoType, payoutMethods],
	);

	const PayoutMethodFields = useCallback(() => {
		switch (paymentInfoType) {
			case PayoutMethodTypes.ACH.value:
				return (
					<FormSection name="ach">
						<AchFields
							loading={loading}
							formName={formName}
							isEditable={isPayoutDetailsEditable}
						/>
					</FormSection>
				);
			case PayoutMethodTypes.DomesticWire.value:
				return (
					<FormSection name="domestic_wire">
						<DomesticWireFields
							loading={loading}
							formName={formName}
							isEditable={isPayoutDetailsEditable}
						/>
					</FormSection>
				);
			case PayoutMethodTypes.InternationalWire.value:
				return (
					<FormSection name="international_wire">
						<InternationalWireFields
							loading={loading}
							formName={formName}
							isEditable={isPayoutDetailsEditable}
						/>
					</FormSection>
				);
			case PayoutMethodTypes.Check.value:
				return (
					<FormSection name="check">
						<CheckFields
							loading={loading}
							formName={formName}
							isEditable={isPayoutDetailsEditable}
						/>
					</FormSection>
				);
			default:
				return null;
		}
	}, [formName, isPayoutDetailsEditable, loading, paymentInfoType]);

	const partnerTypesToWarn = useMemo(
		() => ["AuroraHouseAccount", "OutsideSalesAgent"],
		[],
	);

	const handleResidualsStatusChange = useCallback(
		e => {
			if (partnerTypesToWarn.indexOf(partnerType) >= 0) {
				dispatch(
					uiActions.showWarningNotification({
						title: "Please check corresponding agent office account to see if their details require updating.",
					}),
				);
			}

			if (
				currentUserId !== residualsStatusUpdatedBy?.id &&
				ResidualsStatusTypes.WithholdResiduals.value !==
					parseInt(e.target.value)
			) {
				if (
					residualsStatus &&
					e.target.value &&
					parseInt(e.target.value) !== residualsStatus &&
					!isEmpty(residualsStatusUpdatedBy) &&
					currentUserId !== residualsStatusUpdatedBy?.id
				) {
					const residuals_status = paymentInfo?.residuals_status;
					const withhold_reason = paymentInfo?.withhold_reason;
					dispatch(
						uiActions.showModal("RESIDUALS_STATUS_CHANGING", {
							data: {
								residualsStatus:
									ResidualsStatusTypes.getByValue(
										residualsStatus,
									)?.displayName,
								changedResidualStatusByUser:
									residualsStatusUpdatedBy,
							},
							onCancel() {
								dispatch(
									change(
										formName,
										"payment_info.residuals_status",
										residuals_status,
									),
								);
								dispatch(
									change(
										formName,
										"payment_info.withhold_reason",
										withhold_reason,
									),
								);
							},
						}),
					);
				}
			}

			if (e.target.value !== paymentInfo?.residuals_status) {
				dispatch(
					change(formName, "payment_info.withhold_reason", null),
				);
			}
		},
		[
			currentUserId,
			dispatch,
			formName,
			partnerType,
			partnerTypesToWarn,
			paymentInfo?.residuals_status,
			paymentInfo?.withhold_reason,
			residualsStatus,
			residualsStatusUpdatedBy,
		],
	);

	const isRequiredWithholdReason =
		isResidualsStatusDisabled || !isWithholdResiduals;

	return (
		<FormSection name="payment_info">
			<FormRow>
				<FormColumn>
					<Field
						component={CustomDropdownByValue}
						labelName="displayName"
						smLabel={5}
						smSelect={7}
						label="Residuals Status"
						name="residuals_status"
						items={residualsStatuses}
						loading={loading}
						hideRequiredMark
						disabled={isResidualsStatusDisabled}
						onChange={handleResidualsStatusChange}
					/>
				</FormColumn>
				<FormColumn>
					<Field
						component={CustomDropdownByValue}
						labelName="displayName"
						smLabel={5}
						smSelect={7}
						label="Withhold Reason"
						name="withhold_reason"
						items={withholdStatuses}
						loading={loading}
						hideRequiredMark={isRequiredWithholdReason}
						validate={
							!isRequiredWithholdReason ? [required] : undefined
						}
						disabled={isRequiredWithholdReason}
					/>
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn>
					<FieldWithReadOnlyMode
						component={CustomDropdownByValue}
						labelName="displayName"
						smLabel={5}
						smSelect={7}
						label="Payout Method Type"
						emptyValue="0"
						name="payment_method_type"
						items={payoutMethods}
						loading={loading || isLoadingPayoutMethods}
						hideRequiredMark
						readOnlyProps={{
							isEditable: isPayoutDetailsEditable,
							value: paymentMethodType,
						}}
					/>
				</FormColumn>
			</FormRow>
			<PayoutMethodFields />
			<FormRow>
				<FormColumn>
					<TextWithLoader
						label="Payee ID"
						value={paymentInfo?.number}
						loading={loading}
						labelCol={5}
						valueCol={7}
					/>
				</FormColumn>
			</FormRow>
		</FormSection>
	);
};

const component = memo(PayoutInfoSection);
export { component as PayoutInfoSection };
